import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Modal,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";
import axios from "axios";
import { IconType } from "react-icons";
import { FaEdit, FaPlus, FaSearch, FaSyncAlt, FaTrash } from "react-icons/fa";
import authHeader from "../services/auth-header";

type AlertState = {
  variant: "success" | "danger" | "warning";
  message: string;
};

type PageResponse<T> = {
  content?: T[];
  totalPages?: number;
  totalElements?: number;
  message?: string;
};

export type CatalogItem = {
  id: number;
  name: string;
  description?: string | null;
  [key: string]: any;
};

type CatalogForm = {
  name: string;
  description: string;
};

interface CatalogAdminPageProps {
  title: string;
  subtitle: string;
  singularLabel: string;
  searchPlaceholder: string;
  endpoint: string;
  Icon: IconType;
}

const emptyForm: CatalogForm = {
  name: "",
  description: "",
};

const getPageItems = <T,>(data: PageResponse<T> | T[] | any): T[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  return [];
};

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  (typeof error?.response?.data === "string" ? error.response.data : null) ||
  error?.message ||
  fallback;

const CatalogAdminPage: React.FC<CatalogAdminPageProps> = ({
  title,
  subtitle,
  singularLabel,
  searchPlaceholder,
  endpoint,
  Icon,
}) => {
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<AlertState | null>(null);
  const [modalAlert, setModalAlert] = useState<AlertState | null>(null);
  const [deleteAlert, setDeleteAlert] = useState<AlertState | null>(null);

  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<CatalogItem | null>(null);
  const [form, setForm] = useState<CatalogForm>(emptyForm);

  const [deleteItem, setDeleteItem] = useState<CatalogItem | null>(null);

  const mode = useMemo(() => (editing ? "edit" : "create"), [editing]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setAlert(null);

    try {
      const response = await axios.get<PageResponse<CatalogItem>>(endpoint, {
        headers: authHeader(),
        params: {
          keyword: searchTerm,
          page: currentPage - 1,
          size: pageSize,
        },
      });

      const data = getPageItems<CatalogItem>(response.data);
      setItems(data);
      setTotalPages(Math.max(1, response.data?.totalPages || 1));
    } catch (error: any) {
      setItems([]);
      setTotalPages(1);
      setAlert({
        variant: "danger",
        message: getErrorMessage(error, `No se pudieron cargar ${title.toLowerCase()}.`),
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage, endpoint, searchTerm, title]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalAlert(null);
    setShowModal(true);
  };

  const openEdit = (item: CatalogItem) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      description: item.description || "",
    });
    setModalAlert(null);
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalAlert(null);
    setShowModal(false);
  };

  const saveItem = async () => {
    const name = form.name.trim();
    if (!name) {
      setModalAlert({ variant: "warning", message: "El nombre es requerido." });
      return;
    }

    setSaving(true);
    setModalAlert(null);
    try {
      const payload = {
        name,
        description: form.description.trim(),
      };

      if (mode === "create") {
        await axios.post(endpoint, payload, { headers: authHeader() });
      } else {
        await axios.put(`${endpoint}/${editing?.id}`, { ...payload, id: editing?.id }, { headers: authHeader() });
      }

      setShowModal(false);
      setAlert({
        variant: "success",
        message: mode === "create" ? `${singularLabel} creado correctamente.` : `${singularLabel} actualizado correctamente.`,
      });
      await fetchItems();
    } catch (error: any) {
      setModalAlert({
        variant: "danger",
        message: getErrorMessage(error, `No se pudo guardar ${singularLabel.toLowerCase()}.`),
      });
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (item: CatalogItem) => {
    setDeleteAlert(null);
    setDeleteItem(item);
  };

  const closeDelete = () => {
    if (saving) return;
    setDeleteAlert(null);
    setDeleteItem(null);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;

    setSaving(true);
    setDeleteAlert(null);
    try {
      await axios.delete(`${endpoint}/${deleteItem.id}`, { headers: authHeader() });
      setDeleteItem(null);
      setAlert({ variant: "success", message: `${singularLabel} eliminado correctamente.` });
      await fetchItems();
    } catch (error: any) {
      setDeleteAlert({
        variant: "danger",
        message: getErrorMessage(error, `No se pudo eliminar ${singularLabel.toLowerCase()}. Puede estar en uso.`),
      });
    } finally {
      setSaving(false);
    }
  };

  const search = () => {
    setSearchTerm(tempSearchTerm);
    setCurrentPage(1);
  };

  return (
    <Container className="py-4">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <Row className="align-items-center g-3">
            <Col md={6}>
              <h4 className="mb-1">{title}</h4>
              <div className="text-muted" style={{ fontSize: 13 }}>
                {subtitle}
              </div>
            </Col>

            <Col md={6} className="d-flex justify-content-md-end gap-2">
              <OverlayTrigger placement="top" overlay={<Tooltip>Recargar</Tooltip>}>
                <Button variant="outline-secondary" onClick={fetchItems} disabled={loading}>
                  <FaSyncAlt className="me-2" />
                  Recargar
                </Button>
              </OverlayTrigger>

              <Button variant="primary" onClick={openCreate}>
                <FaPlus className="me-2" />
                Nuevo {singularLabel.toLowerCase()}
              </Button>
            </Col>
          </Row>

          {alert && (
            <Alert className="mt-3" variant={alert.variant} onClose={() => setAlert(null)} dismissible>
              {alert.message}
            </Alert>
          )}

          <Row className="mt-3 g-3">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  className="mt-0"
                  placeholder={searchPlaceholder}
                  value={tempSearchTerm}
                  onChange={(event) => setTempSearchTerm(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") search();
                  }}
                />
                <Button variant="primary" onClick={search}>
                  Buscar
                </Button>
              </InputGroup>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end align-items-center">
              <div className="text-muted" style={{ fontSize: 13 }}>
                Pagina <b>{currentPage}</b> de <b>{totalPages}</b>
              </div>
            </Col>
          </Row>

          <div className="table-responsive mt-3">
            <Table hover bordered className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 70 }}>ID</th>
                  <th>{singularLabel}</th>
                  <th>Descripcion</th>
                  <th style={{ width: 220 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="text-center py-5">
                      <Spinner animation="border" className="me-2" />
                      Cargando...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-5 text-muted">
                      No hay registros para mostrar.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2 fw-semibold">
                          <Icon />
                          {item.name}
                        </div>
                      </td>
                      <td className="text-muted">{item.description || "-"}</td>
                      <td>
                        <div className="d-flex flex-wrap gap-2">
                          <Button variant="outline-primary" size="sm" onClick={() => openEdit(item)}>
                            <FaEdit className="me-1" />
                            Editar
                          </Button>
                          <Button variant="outline-danger" className="btn-delete" size="sm" onClick={() => askDelete(item)}>
                            <FaTrash className="me-1" />
                            Eliminar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={currentPage <= 1 || loading}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            >
              Anterior
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={currentPage >= totalPages || loading}
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
            >
              Siguiente
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={closeModal} centered backdrop={saving ? "static" : true}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>{mode === "create" ? `Nuevo ${singularLabel.toLowerCase()}` : `Editar ${singularLabel.toLowerCase()}`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalAlert && (
            <Alert variant={modalAlert.variant} onClose={() => setModalAlert(null)} dismissible>
              {modalAlert.message}
            </Alert>
          )}

          <Row className="g-3">
            <Col md={12}>
              <Form.Label>Nombre *</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                disabled={saving}
              />
            </Col>
            <Col md={12}>
              <Form.Label>Descripcion</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.description}
                onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                disabled={saving}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={saveItem} disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Guardando...
              </>
            ) : mode === "create" ? (
              "Crear"
            ) : (
              "Guardar"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!deleteItem} onHide={closeDelete} centered backdrop={saving ? "static" : true}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Confirmar eliminacion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteAlert && (
            <Alert variant={deleteAlert.variant} onClose={() => setDeleteAlert(null)} dismissible>
              {deleteAlert.message}
            </Alert>
          )}
          Seguro que desea eliminar <b>{deleteItem?.name}</b>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDelete} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={saving}>
            {saving ? "Eliminando..." : "Eliminar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CatalogAdminPage;
